import type { CodeSession } from '../mock-sessions';

import { parseRepositoryFullName } from '@/utils/github/repository-full-name';

export type PrRef = {
  owner: string;
  repo: string;
  number: number;
};

const COMPACT_PR_REF = /^([\w.-]+)\/([\w.-]+)#(\d+)$/;
const PATH_PR_REF = /^\/(?:(?:pr|pr-v2)\/)?([\w.-]+)\/([\w.-]+)\/pull\/(\d+)(?:\/|$)/;

const CUBIC_PR_HOSTS = new Set([
  'cubic.dev',
  'www.cubic.dev',
  'app.cubic.dev',
  'localhost',
  '127.0.0.1',
  '[::1]',
]);

function decodePrInput(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function toPrRef(owner: string, repo: string, numberText: string): PrRef | null {
  const number = Number.parseInt(numberText, 10);
  if (!Number.isFinite(number) || number <= 0) {
    return null;
  }

  return { owner, repo, number };
}

function extractPathname(value: string): string | null {
  if (value.startsWith('/')) {
    return value.split(/[?#]/, 1)[0] ?? null;
  }

  const candidate = value.includes('://') ? value : `https://${value}`;
  try {
    return new URL(candidate).pathname;
  } catch {
    return null;
  }
}

export function formatPrRef(ref: PrRef): string {
  return `${ref.owner}/${ref.repo}#${ref.number}`;
}

export function parsePrRef(value: string): PrRef | null {
  const trimmed = decodePrInput(value.trim());
  if (!trimmed) {
    return null;
  }

  const compact = trimmed.match(COMPACT_PR_REF);
  if (compact?.[1] && compact[2] && compact[3]) {
    return toPrRef(compact[1], compact[2], compact[3]);
  }

  const pathname = extractPathname(trimmed);
  if (!pathname) {
    return null;
  }

  const fromPath = pathname.match(PATH_PR_REF);
  if (fromPath?.[1] && fromPath[2] && fromPath[3]) {
    return toPrRef(fromPath[1], fromPath[2], fromPath[3]);
  }

  return null;
}

export function parsePrLink(href: string): PrRef | null {
  let url: URL;
  try {
    url = new URL(href, 'https://cubic.dev');
  } catch {
    return null;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return null;
  }

  if (url.hostname === 'github.com') {
    if (!/^\/[\w.-]+\/[\w.-]+\/pull\//.test(url.pathname)) {
      return null;
    }
  } else if (!CUBIC_PR_HOSTS.has(url.hostname) || !/^\/(?:pr|pr-v2)\//.test(url.pathname)) {
    return null;
  }

  return parsePrRef(url.pathname);
}

/** Whether two refs name the same pull request; GitHub owner and repo names are case-insensitive. */
export function samePrRef(a: PrRef, b: PrRef): boolean {
  return (
    a.number === b.number &&
    a.owner.toLowerCase() === b.owner.toLowerCase() &&
    a.repo.toLowerCase() === b.repo.toLowerCase()
  );
}

/** Pull request `number` of the repository named `repoFullName` (`owner/repo`); null for any other name. */
export function repositoryPrRef(repoFullName: string, number: number): PrRef | null {
  const repository = parseRepositoryFullName(repoFullName);
  return repository ? { ...repository, number } : null;
}

export function sessionPrRef(session: Pick<CodeSession, 'pullRequest'>): PrRef | null {
  return session.pullRequest ? parsePrLink(session.pullRequest.url) : null;
}

export function cubicPrPath(ref: PrRef): string {
  return `/pr/${ref.owner}/${ref.repo}/pull/${ref.number}`;
}
